import * as Sentry from '@sentry/browser';

/*
 * The plan allows 1,000 events a month for the whole product, backend included.
 * Every decision below exists to keep the browser from spending a budget that
 * server errors also draw on:
 *
 *  - no tracing and no session replay: transactions alone consumed 74% of one
 *    period's quota on the backend before they were turned off;
 *  - nothing is sent from dev, so a hot-reload loop cannot drain the month;
 *  - the noise below is dropped in the browser, because a filtered event still
 *    costs an event once it arrives.
 *
 * The DSN is an ingest key. It ships in the bundle by design and grants writes
 * only — it cannot read an issue.
 */

const DSN = import.meta.env.VITE_GLITCHTIP_DSN;

/* Browser noise that says nothing about our code. */
const IGNORED = [
    /ResizeObserver loop/i,
    /Non-Error promise rejection captured/i,
    /^Network ?Error$/i,
    /Failed to fetch/i,
    /Load failed/i,
    /NetworkError when attempting to fetch resource/i,
    /AbortError/i,
    /The operation was aborted/i,
    /cancelled|canceled/i,
    /Script error\.?$/,
    /ChunkLoadError/i,
    /Loading chunk \d+ failed/i,
    /Loading CSS chunk/i,
];

/* Anything not served by us — extensions, injected widgets, bookmarklets. */
const DENIED_URLS = [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
];

export function initErrorReporting() {
    if (!DSN || !import.meta.env.PROD) {
        return false;
    }

    Sentry.init({
        dsn: DSN,
        environment: import.meta.env.VITE_ENVIRONMENT || 'production',
        release: import.meta.env.VITE_RELEASE || undefined,
        /* Errors only. Adding browserTracingIntegration here would reintroduce
         * exactly the spend that had to be removed from the backend. */
        tracesSampleRate: 0,
        sampleRate: 1.0,
        ignoreErrors: IGNORED,
        denyUrls: DENIED_URLS,
        /* A dead session token is an expected round trip, not a fault: the
         * session guard already logs the user out and says so on screen. */
        beforeSend(event) {
            const status = event.extra && event.extra.status;
            if (status === 401 || status === 403) {
                return null;
            }
            return event;
        },
    });
    return true;
}

/* Vue swallows render/lifecycle errors into its own handler, so they never
 * reach window.onerror and would otherwise go unreported. */
export function installVueErrorHandler(app) {
    const existing = app.config.errorHandler;
    app.config.errorHandler = (error, instance, info) => {
        Sentry.withScope((scope) => {
            scope.setContext('vue', {
                lifecycleHook: info,
                component: instance?.$options?.name || instance?.$?.type?.name || 'anonymous',
            });
            Sentry.captureException(error);
        });
        if (typeof existing === 'function') {
            existing(error, instance, info);
        } else {
            console.error(`[vue] ${info}:`, error);
        }
    };
}
